import { useMemo, useRef, useEffect } from 'react'
import ReactApexChart from 'react-apexcharts'
import dayjs from 'dayjs'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import useMobile from '../../../../hooks/useMobile'

const TechnicalGraphContainer = ({
    openTable,
    dynamicHeadings,
    mapInverterSeries,
    selectedButtons,
    yaxisUnit,
    inverterviewbutton,
    technicalToDate,
    technicalFromDate,
}) => {
    const isMobile = useMobile()
    const scrollRef = useRef(null)

    // Calculate dynamic axis constraints for 'day' view
    const axisConstraints = useMemo(() => {
        if (inverterviewbutton !== 'day') {
            return { min: undefined, max: undefined, tickAmount: isMobile ? 15 : undefined };
        }

        const startOfDay = dayjs(technicalFromDate).startOf('day');
        const minHour = 5;
        const maxHour = 19;
        const min = startOfDay.add(minHour, 'hour').valueOf(); // Start at 5:00 AM

        // For 'day' view, we align max to the next full hour to ensure ticks land on clean boundaries
        const isToday = dayjs().isSame(dayjs(technicalFromDate), 'day');
        let max;
        if (isToday) {
            // Align to the next full hour, but not exceeding 7:00 PM
            const nextHour = dayjs().add(1, 'hour').startOf('hour');
            max = Math.min(nextHour.valueOf(), startOfDay.add(maxHour, 'hour').valueOf());
        } else {
            max = startOfDay.add(maxHour, 'hour').valueOf();
        }

        // Ensure at least 1 hour duration
        if (max <= min) max = startOfDay.add(minHour + 1, 'hour').valueOf();

        // Calculate duration in hours to set appropriate tick amount for clean hourly labels
        const durationHours = (max - min) / (1000 * 60 * 60);

        //ensures tick for every hour
        const tickAmount = Math.max(Math.round(durationHours), 2);

        return { min, max, tickAmount };
    }, [inverterviewbutton, technicalFromDate, isMobile]);

    // Ensure mapInverterSeries is always defined
    const validMapInverterSeries = useMemo(() => {
        return mapInverterSeries || []
    }, [mapInverterSeries])

    const hasDualAxes = useMemo(() => {
        const hasEnergyData = validMapInverterSeries.some((series) =>
            ['kWh', 'kW'].includes(series.unit)
        )
        const hasIrradianceData = validMapInverterSeries.some(
            (series) => series.unit === 'kW/m²'
        )
        return hasEnergyData && hasIrradianceData
    }, [validMapInverterSeries])

    const processedData = useMemo(() => {
        if (!validMapInverterSeries || validMapInverterSeries.length === 0) {
            return []
        }

        const baseDate = dayjs(technicalFromDate).isValid()
            ? dayjs(technicalFromDate).startOf('day')
            : dayjs().startOf('day')

        return validMapInverterSeries.map((series) => {
            const validatedData = (series.data || [])
                .map((point) => {
                    let date
                    if (inverterviewbutton === 'day') {
                        // Ensure we handle timestamp correctly for high resolution
                        date = dayjs(point.x)
                    } else {
                        date = dayjs(point.x)
                    }

                    if (!date.isValid()) {
                        return { x: null, y: point.y || 0 }
                    }
                    const yVal = Number(point.y);
                    return { x: date.valueOf(), y: isFinite(yVal) ? yVal : 0 }
                })
                .filter((point) => point.x !== null)

            return {
                name: series.name,
                color: series.color,
                data: validatedData,
                unit: series.unit,
                yAxisId: series.yAxisId,
            }
        })
    }, [validMapInverterSeries, inverterviewbutton, technicalFromDate])

    const chartSeries = useMemo(() => {
        return processedData.map((series) => ({
            ...series,
            ...(hasDualAxes && {
                yAxisIndex: ['kWh', 'kW'].includes(series.unit) ? 0 : 1,
            }),
        }))
    }, [processedData, hasDualAxes])

    const chartOptions = useMemo(() => {
        const getXAxisFormatter = (value, _, opts) => {
            const date = dayjs(value);
            if (!date.isValid()) return '';

            const range = opts?.xaxis ? opts.xaxis.max - opts.xaxis.min : 0;
            const isShortRange = range > 0 && range <= 36 * 60 * 60 * 1000;

            // Helper to snap time to nearest interval and check tolerance
            const getSnappedLabel = (intervalMins, formatStr = 'HH:mm') => {
                const totalMins = date.hour() * 60 + date.minute() + date.second() / 60;

                // Find nearest multiple of intervalMins
                const nearest = Math.round(totalMins / intervalMins) * intervalMins;
                const dist = Math.abs(totalMins - nearest);

                // Adaptive Tolerance:
                // If we rely on ticks that are e.g. 4 mins apart (zoomed in), and want 15 min labels:
                // Ticks: 00, 04, 08, 12, 16...
                // Target: 15.
                // 12 is 3 away. 16 is 1 away. We want to catch 16 and call it 15.
                // If tolerance is too low (e.g. 10% of 15 = 1.5), distance 1 is OK.
                // If interval is 60 (ticks 00, 00... wait, if zoomed out ticks are hourly).

                // We allow a generous absolute tolerance of ~20% of the interval
                // But cap it to ensure we don't overlap (e.g. 30m interval shouldn't snap 20m away).
                const tolerance = Math.max(2.5, intervalMins * 0.2);

                if (dist <= tolerance) {
                    const h = Math.floor(nearest / 60) % 24;
                    const m = Math.floor(nearest % 60);
                    return dayjs().hour(h).minute(m).format(formatStr);
                }
                return '';
            };

            // 1. Dynamic Formatter for 'day' view (Today)
            if (inverterviewbutton === 'day') {
                const rangeHours = range / (3600 * 1000);

                // Default / Zoom Out (> 3 hours): Interval 60 mins
                // Catches 5:03 -> 5:00
                if (rangeHours > 3) {
                    return getSnappedLabel(60, 'HH:00');
                }
                // Zoom In Level 1 (1.5h < Range <= 3h): 30 minutes
                else if (rangeHours > 1.5) {
                    return getSnappedLabel(30);
                }
                // Zoom In Level 2 (0.5h < Range <= 1.5h): 15 minutes
                else if (rangeHours > 0.5) {
                    return getSnappedLabel(15);
                }
                // Zoom In Level 3 (0.1h < Range <= 0.5h): 2 minutes
                else if (rangeHours > 0.1) {
                    return getSnappedLabel(2);
                }
                // Zoom In Level 4 (Range <= 0.1h): 1 minute
                else {
                    return date.format('HH:mm'); // No snapping needed at lowest level
                }
            }

            // 2. Original Logic for Custom Short Range
            else if (inverterviewbutton === 'custom' && isShortRange) {
                const mins = date.minute();
                const totalMins = mins + date.second() / 60;
                const isAligned = (target) => Math.abs(totalMins - target) < 0.5;

                if (range > 6 * 60 * 60 * 1000) {
                    return isAligned(0) ? date.format('HH:00') : '';
                } else if (range > 2 * 60 * 60 * 1000) {
                    return (isAligned(0) || isAligned(30)) ? date.format('HH:mm') : '';
                } else {
                    return (isAligned(0) || isAligned(15) || isAligned(30) || isAligned(45)) ? date.format('HH:mm') : '';
                }
            }

            // 3. Logic for other periods
            else if (['MTD', '1M', '3M', 'custom'].includes(inverterviewbutton)) {
                return date.format('DD/MM');
            } else if (['6M', '1Y', 'YTD'].includes(inverterviewbutton)) {
                return date.format('MM-YY');
            }
            return date.format('DD/MM');
        }

        const options = {
            chart: {
                height: 350,
                type: 'line',
                zoom: { enabled: true },
                toolbar: { show: !isMobile },
                animations: {
                    enabled: false
                }
            },
            legend: { show: true, position: isMobile ? 'top' : 'bottom' },
            dataLabels: { enabled: false },
            stroke: { curve: 'straight', width: 2 },
            markers: { size: 0, hover: { size: 5 } },
            grid: {
                borderColor: '#f1f1f1',
                xaxis: { lines: { show: true } }
            },
            xaxis: {
                type: 'datetime',
                minTickInterval: undefined, // Allow small intervals for zoom
                tickAmount: axisConstraints.tickAmount,
                labels: {
                    rotate: -45,
                    style: { fontSize: '10px' },
                    formatter: getXAxisFormatter,
                    datetimeFormatter: {
                        year: 'yyyy',
                        month: 'MMM \'yy',
                        day: 'dd MMM',
                        hour: 'HH:mm',
                    }
                },
                min: axisConstraints.min,
                max: axisConstraints.max,
                tooltip: {
                    enabled: false
                }
            },
            tooltip: {
                shared: true,
                x: {
                    show: true,
                    formatter: (val) => `Time: ${dayjs(val).format('HH:mm')}`,
                },
                y: {
                    formatter: (value, { seriesIndex }) => {
                        const unit = chartSeries[seriesIndex]?.unit || yaxisUnit
                        const formattedValue = isFinite(value) ? value.toFixed(2) : '0.00'
                        return `${formattedValue} ${unit}`
                    },
                },
            },
        }

        if (hasDualAxes) {
            options.yaxis = [
                {
                    title: { text: 'Energy (kWh/kW)' },
                    labels: { formatter: (v) => isFinite(v) ? v.toFixed(1) : '0.0' },
                    min: 0
                },
                {
                    opposite: true,
                    title: { text: 'Irrad. (kW/m²)' },
                    labels: { formatter: (v) => isFinite(v) ? v.toFixed(2) : '0.00' }
                }
            ]
        } else {
            options.yaxis = {
                title: { text: yaxisUnit },
                labels: { formatter: (v) => isFinite(v) ? v.toFixed(1) : '0.0' }
            }
        }

        return options
    }, [hasDualAxes, yaxisUnit, chartSeries, isMobile, inverterviewbutton])

    useEffect(() => {
        if (isMobile && scrollRef.current && processedData.length > 0 && processedData[0].data.length > 0) {
            setTimeout(() => {
                if (scrollRef.current) {
                    scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
                }
            }, 500)
        }
    }, [isMobile, processedData])

    return (
        <div>
            {openTable ? (
                <div style={{ maxHeight: '360px', overflow: 'auto' }}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time</TableCell>
                                    {dynamicHeadings?.map((h, i) => <TableCell key={i}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedData[0]?.data.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        <TableCell>{dayjs(row.x).format('DD/MM HH:mm')}</TableCell>
                                        {processedData.map((item, colIndex) => (
                                            <TableCell key={colIndex}>
                                                {item.data[rowIndex] ? item.data[rowIndex].y.toFixed(2) : '-'}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            ) : (
                <div
                    ref={scrollRef}
                    style={{
                        overflowX: isMobile ? 'auto' : 'hidden',
                        width: '100%',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <div style={{ width: isMobile ? '800px' : '100%', minHeight: '360px' }}>
                        <ReactApexChart
                            key={`technical-chart-${chartSeries.map(s => s.name).join('-')}-${hasDualAxes}-${inverterviewbutton}`}
                            options={chartOptions}
                            series={chartSeries}
                            type="line"
                            height={350}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default TechnicalGraphContainer
